import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SignalsFeedPage, useIsMobile } from "./SignalsFeedPage.js";
import { Card } from "@bios/ui";
import { useApiAuthed, useApiDev } from "../auth.js";
import type {
  AccountabilityActor,
  CoverageInfo,
  ForwardProjection,
  GapDetail,
  GapSummary,
  ImpactByLocation,
  LowDataWarning,
  RootCause,
  SystemCausality,
  SystemImpactKpi,
  Urgency,
  OverviewResponse,
  OverviewModuleView,
  OverviewTrendPoint,
} from "../api.js";
import {
  GeoSelector,
  loadGeoSelection,
  type GeoSelection,
} from "../components/GeoSelector.js";
import {
  classifyIntelligenceMode,
  type IntelligenceModeResolved,
} from "../lib/intelligenceModes.js";

const ROLE_LABEL: Record<string, string> = {
  funder: "Funder",
  policy_actor: "Policy actor",
  institution: "Institution",
  amplifier: "Amplifier",
  originator: "Originator",
};

const GAP_LABEL: Record<string, string> = {
  funding_not_reaching_black_communities: "Funding not reaching Black communities",
  policy_without_black_outcome: "Policy without Black outcome",
  narrative_vs_reality_black: "Narrative outpacing reality",
  education_outcome_gap_black: "Education outcomes missing",
  workforce_without_outcome: "Workforce outcomes missing",
  training_without_placement: "Training without placement",
  hiring_without_retention: "Hiring without retention",
  wage_gap_persistence: "Wage gap persistence",
  layoffs_without_recovery: "Layoffs without recovery",
  funding_without_jobs: "Funding without jobs",
  enforcement_without_outcome: "Legal enforcement without outcome",
  legal_action_without_reform: "Legal action without reform",
  rights_restriction_gap: "Rights restriction unresolved",
  policing_without_accountability: "Policing without accountability",
  settlement_without_change: "Settlement without change",
  convergence_without_outcome: "Convergence without outcome",
  multi_module_failure: "Multi-module failure",
  repeated_policy_without_result: "Repeated policy without result",
  recurring_legal_without_reform: "Recurring legal action without reform",
  black_impact_gap: "Black-relevant signals stuck at announcement",
};

interface PageProps {
  devModeNoAuth?: boolean;
}

const WINDOWS: Array<{ key: string; label: string }> = [
  { key: "7d", label: "7d" },
  { key: "14d", label: "14d" },
  { key: "30d", label: "30d" },
];

const AUTO_EXEC_KEY = "bios:auto-exec";

// Phase 25: thin router that picks SignalsFeedPage on mobile (unless
// the user explicitly requested Intelligence via ?view=intelligence)
// or OverviewDesktop otherwise. Two hooks always called in the same
// order — the inner desktop component owns the rest of the hook chain.
export function Overview({ devModeNoAuth }: PageProps) {
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const forceIntelligence = searchParams.get("view") === "intelligence";
  if (isMobile && !forceIntelligence) {
    return <SignalsFeedPage devModeNoAuth={devModeNoAuth} />;
  }
  return <OverviewDesktop devModeNoAuth={devModeNoAuth} />;
}

function OverviewDesktop({ devModeNoAuth }: PageProps) {
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const navigate = useNavigate();
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [windowKey, setWindowKey] = useState("7d");
  // Phase 20: default surface filters out general/unknown population items.
  const [includeGeneral, setIncludeGeneral] = useState(false);
  // Phase 21: persistent geo scope (national / state / city).
  const [geo, setGeo] = useState<GeoSelection>(() => loadGeoSelection());

  // Phase 23.4: auto-execute the top action on first session visit when
  // measurable Black impact is zero. Runs once per session; subsequent
  // visits to /app stay on Overview so the user can see the diagnostic
  // surface they just navigated back to.
  useEffect(() => {
    if (!data) return;
    let alreadyAuto = false;
    try {
      alreadyAuto = sessionStorage.getItem(AUTO_EXEC_KEY) === "1";
    } catch {
      /* private mode / quota — fall back to no auto-exec */
      alreadyAuto = true;
    }
    if (alreadyAuto) return;
    const measurable = data.system_impact?.measurable_black_items ?? 0;
    if (measurable > 0) return;
    const top = data.focus_recommendations?.[0];
    if (!top?.query) return;
    try {
      sessionStorage.setItem(AUTO_EXEC_KEY, "1");
    } catch {
      /* ignore */
    }
    navigate(
      `/app/command?q=${encodeURIComponent(top.query)}&auto=1`,
      { replace: true },
    );
  }, [data, navigate]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .overview(windowKey, {
        includeGeneral,
        geoScope: geo.scope === "national" ? undefined : geo.scope,
        geoState: geo.scope !== "national" ? geo.state : undefined,
        geoCity: geo.scope === "city" ? geo.city : undefined,
      })
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowKey, includeGeneral, geo.scope, geo.state, geo.city]);

  return (
    <DesktopConsole
      data={data}
      error={error}
      loading={loading}
      geo={geo}
      setGeo={setGeo}
      includeGeneral={includeGeneral}
      setIncludeGeneral={setIncludeGeneral}
      windowKey={windowKey}
      setWindowKey={setWindowKey}
    />
  );
}

// Phase 36R — Desktop console. Top bar + 3-column grid + expandable
// evidence drawer at the bottom. Mobile is routed away from this
// component entirely by <Overview/> via useIsMobile().
function DesktopConsole({
  data,
  error,
  loading,
  geo,
  setGeo,
  includeGeneral,
  setIncludeGeneral,
  windowKey,
  setWindowKey,
}: {
  data: OverviewResponse | null;
  error: string | null;
  loading: boolean;
  geo: GeoSelection;
  setGeo: (g: GeoSelection) => void;
  includeGeneral: boolean;
  setIncludeGeneral: (v: boolean) => void;
  windowKey: string;
  setWindowKey: (k: string) => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const mode = useMemo(
    () => (data ? classifyIntelligenceMode(data) : null),
    [data],
  );
  const rankedSignals = useMemo(() => (data ? rankSignals(data) : []), [data]);

  function submitQuery(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/app/command?q=${encodeURIComponent(q)}`);
  }

  return (
    <div className="bios-console">
      <header className="bios-console-topbar" role="banner">
        <div className="bios-console-brand">
          <span className="bios-console-brand-mark">BIOS</span>
          <span className="bios-console-brand-sep">·</span>
          <span className="bios-console-brand-label">Intelligence Console</span>
        </div>
        <form className="bios-console-cmd" onSubmit={submitQuery}>
          <span className="bios-console-cmd-prompt" aria-hidden>
            ⌕
          </span>
          <input
            type="text"
            className="bios-console-cmd-input"
            placeholder="Ask intelligence — entity, module, region, or question"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Command entry"
          />
          <kbd className="bios-console-cmd-hint">↵</kbd>
        </form>
        <div className="bios-console-controls">
          <GeoSelector value={geo} onChange={setGeo} />
          <div className="bios-console-windows" role="group" aria-label="Time window">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                type="button"
                className={`bios-console-window${windowKey === w.key ? " active" : ""}`}
                onClick={() => setWindowKey(w.key)}
              >
                {w.label}
              </button>
            ))}
          </div>
          <label className="bios-console-toggle">
            <input
              type="checkbox"
              checked={includeGeneral}
              onChange={(e) => setIncludeGeneral(e.target.checked)}
            />
            <span>General pop.</span>
          </label>
        </div>
      </header>

      {error && (
        <div className="bios-console-error" role="alert">
          {error}
        </div>
      )}

      {loading && !data && <LoadingHero />}

      {data && mode && (
        <>
          {data.low_data_warning && (
            <LowDataBanner
              warning={data.low_data_warning}
              onExpand={() => setGeo({ scope: "national" })}
            />
          )}
          <div className="bios-console-grid">
            <PriorityBrief
              active={rankedSignals[0] ?? null}
              mode={mode}
              urgency={data.urgency}
              recs={data.focus_recommendations}
            />
            <LiveSignals signals={rankedSignals} />
            <SystemContext data={data} mode={mode} />
          </div>
          <EvidenceDrawer data={data} />
        </>
      )}
    </div>
  );
}

// LEFT — Priority Brief. Dominant system mode, one headline, one short
// explanation, three top actions. Same data shape as Phase 36; tighter
// chrome.
function PriorityBrief({
  active,
  mode,
  urgency,
  recs,
}: {
  active: RankedSignal | null;
  mode: IntelligenceModeResolved;
  urgency?: Urgency;
  recs: import("../api.js").FocusRecommendation[];
}) {
  const urgencyTag =
    urgency?.level === "critical"
      ? "Critical"
      : urgency?.level === "elevated"
      ? "Elevated"
      : null;
  const top = recs.slice(0, 3);
  return (
    <section
      className={`bios-col bios-col-priority bios-col-tone-${mode.tone}`}
      aria-label="Priority brief"
    >
      <div className="bios-col-eyebrow">Priority brief</div>
      <div className="bios-priority-mode-row">
        <span className={`bios-priority-mode bios-priority-mode-${mode.tone}`}>
          {mode.label}
        </span>
        {urgencyTag && (
          <span className="bios-priority-urgency">{urgencyTag}</span>
        )}
      </div>
      <h2 className="bios-priority-headline">{mode.headline}</h2>
      <p className="bios-priority-subline">{mode.subline}</p>
      {active && (
        <Link
          to={active.href}
          className={`bios-priority-active bios-priority-active-${active.tone}`}
        >
          <span className="bios-priority-active-kicker">Active priority</span>
          <strong>{active.headline}</strong>
          <small>{active.reason}</small>
        </Link>
      )}
      {mode.focus && (
        <div className="bios-priority-focus">
          <span className="bios-priority-focus-label">Dominant movement</span>
          <span className="bios-priority-focus-text">{mode.focus}</span>
        </div>
      )}
      {top.length > 0 && (
        <ol className="bios-priority-actions">
          {top.map((r, i) => (
            <li key={i}>
              <Link
                to={`/app/command?q=${encodeURIComponent(r.query)}`}
                className="bios-priority-action"
              >
                <span className="bios-priority-action-num">{i + 1}</span>
                <span className="bios-priority-action-label">{r.label}</span>
                <span className="bios-priority-action-arrow" aria-hidden>→</span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// CENTER — Live Signals. Top 5 ranked signals assembled from clusters,
// near-impact items, and top insights. Each row: tag + headline +
// module + reason. Click routes to Command.
//
// Phase 37 — only strategic convergence clusters surface here. Narrative,
// procedural, and symbolic clusters are computed at the API layer
// (workers/api db.ts) and tagged via convergence_type; this center
// column suppresses them so the dominant intelligence read is real
// system movement, not incidental overlap.
type SignalTag = "CONVERGENCE" | "NEAR-IMPACT" | "INSIGHT" | "GAP";
interface RankedSignal {
  key: string;
  tag: SignalTag;
  tone: "active" | "watch" | "blocked";
  headline: string;
  module: string;
  reason: string;
  href: string;
  external: boolean;
  relevance: number;
}

function rankSignals(data: OverviewResponse): RankedSignal[] {
  const out: RankedSignal[] = [];
  const seen = new Set<string>();
  const clusters = data.signal_clusters ?? [];
  for (const c of clusters) {
    // Phase 37 — surface only strategic convergence in Live Signals.
    // Other types still exist in the raw clusters payload for any
    // diagnostic surface that wants them.
    if (c.convergence_type && c.convergence_type !== "strategic") continue;
    const key = `cluster-${c.cluster_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const geo = c.geo.city
      ? `${c.geo.city}${c.geo.state ? ", " + c.geo.state : ""}`
      : c.geo.state ?? "system-wide";
    const q = `What is driving the ${c.modules.join(" + ")} convergence in ${geo}?`;
    out.push({
      key,
      tag: "CONVERGENCE",
      tone: "active",
      headline: c.headline,
      module: c.modules.join(" · "),
      reason: `${c.event_count} events · ${c.modules.length} modules · ${geo}`,
      href: `/app/command?q=${encodeURIComponent(q)}`,
      external: false,
      relevance: Math.min(
        1,
        (c.strategic_relevance_score ?? 0.6) + (c.modules.includes("legal") ? 0.12 : 0),
      ),
    });
    if (out.length >= 4) break;
  }
  for (const n of (data.near_impact ?? []).slice(0, 4)) {
    const key = `near-${n.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const reason =
      n.impact_state === "funded"
        ? "missing deployment evidence"
        : n.impact_state === "deployed"
        ? "missing outcome data"
        : n.impact_state === "active"
        ? "stuck before deployment"
        : "near impact — no outcome yet";
    // Near-impact items: assigned relevance based on impact_state.
    // funded/deployed/outcome_observed land high; "active" middling.
    const nearRelevance =
      n.impact_state === "outcome_observed"
        ? 0.85
        : n.impact_state === "enforced" || n.impact_state === "deployed"
        ? 0.75
        : n.impact_state === "funded"
        ? 0.65
        : 0.55;
    out.push({
      key,
      tag: "NEAR-IMPACT",
      tone: "watch",
      headline: n.title,
      module: n.module,
      reason,
      href: `/app/command?q=${encodeURIComponent(`What outcomes followed this ${n.module} item: ${n.title}`)}`,
      external: false,
      relevance: Math.min(1, nearRelevance + (n.module === "legal" ? 0.12 : 0)),
    });
    if (out.length >= 10) break;
  }
  if (out.length < 10) {
    for (const i of (data.top_insights ?? []).slice(0, 8)) {
      const key = `ins-${i.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        key,
        tag: "INSIGHT",
        tone: "active",
        headline: i.title,
        module: i.module,
        reason: shortReason(i.why_matters),
        href: `/app/command?q=${encodeURIComponent(`Investigate: ${i.title}`)}`,
        external: false,
        relevance: Math.min(
          1,
          (i.cross_module_priority_score ??
            (typeof i.reality_score === "number" ? i.reality_score : 0.5)) +
            (i.module === "legal" ? 0.1 : 0),
        ),
      });
      if (out.length >= 10) break;
    }
  }
  if (out.length < 10) {
    for (const g of (data.gap_summary?.by_kind ?? []).slice(0, 8)) {
      const key = `gap-${g.kind}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const gapRelevance =
        g.severity === "critical"
          ? 0.7
          : g.severity === "high"
          ? 0.55
          : g.severity === "medium"
          ? 0.4
          : 0.25;
      out.push({
        key,
        tag: "GAP",
        tone: "blocked",
        headline: GAP_LABEL[g.kind] ?? g.kind.replace(/_/g, " "),
        module: g.modules.join(" · ") || "system",
        reason: g.why ?? `${g.count} unresolved`,
        href: `/app/command?q=${encodeURIComponent(g.query)}`,
        external: false,
        relevance: Math.min(1, gapRelevance + (g.modules.includes("legal") ? 0.12 : 0)),
      });
      if (out.length >= 10) break;
    }
  }
  // Phase 37 — sort by relevance so the highest-leverage signal lands
  // at the top of Live Signals regardless of source kind.
  out.sort((a, b) => b.relevance - a.relevance);
  return out.slice(0, 10);
}

function shortReason(text: string): string {
  if (!text) return "no decisive signal";
  const trimmed = text.replace(/[.;:].*$/, "").trim();
  const words = trimmed.split(/\s+/);
  return words.length > 14 ? words.slice(0, 14).join(" ") + "…" : trimmed;
}

function LiveSignals({ signals }: { signals: RankedSignal[] }) {
  return (
    <section className="bios-col bios-col-signals" aria-label="Live signals">
      <div className="bios-col-eyebrow">Live signals</div>
      {signals.length === 0 ? (
        <p className="bios-signals-empty">
          No ranked signals in this window. Adjust geo or expand the time
          window above.
        </p>
      ) : (
        <ol className="bios-signals-list">
          {signals.map((s) => (
            <li key={s.key} className={`bios-signal bios-signal-${s.tone}`}>
              {s.external ? (
                <a
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="bios-signal-link"
                >
                  <SignalBody s={s} />
                </a>
              ) : (
                <Link to={s.href} className="bios-signal-link">
                  <SignalBody s={s} />
                </Link>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function SignalBody({ s }: { s: RankedSignal }) {
  return (
    <>
      <div className="bios-signal-meta">
        <span className={`bios-signal-tag bios-signal-tag-${s.tone}`}>{s.tag}</span>
        <span className="bios-signal-module">{s.module}</span>
      </div>
      <div className="bios-signal-headline">{s.headline}</div>
      <div className="bios-signal-reason">{s.reason}</div>
    </>
  );
}

// RIGHT — System Context. Four quiet blocks: strongest convergence,
// closest to impact, dominant gap, accountability actor.
function SystemContext({
  data,
  mode,
}: {
  data: OverviewResponse;
  mode: IntelligenceModeResolved;
}) {
  const convergence =
    data.strongest_active_cluster ??
    data.strongest_geo_concentration ??
    (data.signal_clusters?.[0] ?? null);
  const closest = (data.near_impact ?? [])[0] ?? null;
  const gap = (data.gap_summary?.by_kind ?? [])[0] ?? null;
  const actor = (data.accountability?.actors ?? [])[0] ?? null;
  const empty = !convergence && !closest && !gap && !actor;
  return (
    <aside className="bios-col bios-col-context" aria-label="System context">
      <div className="bios-col-eyebrow">System context</div>
      {empty ? (
        <p className="bios-context-empty">
          No convergence, near-impact, gap, or accountability signal in this
          window.
        </p>
      ) : (
        <div className="bios-context-blocks">
          {convergence && (
            <ContextBlock label="Strongest convergence">
              <div className="bios-context-headline">{convergence.headline}</div>
              <div className="bios-context-meta">
                {convergence.modules.join(" · ")}
                {convergence.geo.city || convergence.geo.state ? (
                  <>
                    {" · "}
                    {convergence.geo.city
                      ? `${convergence.geo.city}${
                          convergence.geo.state
                            ? ", " + convergence.geo.state
                            : ""
                        }`
                      : convergence.geo.state}
                  </>
                ) : null}
              </div>
            </ContextBlock>
          )}
          {closest && (
            <ContextBlock label="Closest to impact">
              <div className="bios-context-headline">
                {closest.url ? (
                  <a
                    href={closest.url}
                    target="_blank"
                    rel="noreferrer"
                    className="bios-context-link"
                  >
                    {closest.title}
                  </a>
                ) : (
                  closest.title
                )}
              </div>
              <div className="bios-context-meta">
                {closest.module}
                {closest.impact_state && (
                  <>
                    {" · "}
                    {closest.impact_state}
                  </>
                )}
              </div>
            </ContextBlock>
          )}
          {gap && (
            <ContextBlock label="Dominant gap">
              <Link
                to={`/app/command?q=${encodeURIComponent(gap.query)}`}
                className="bios-context-link"
              >
                <div className="bios-context-headline">
                  {GAP_LABEL[gap.kind] ?? gap.kind.replace(/_/g, " ")}
                </div>
              </Link>
              <div className="bios-context-meta">
                {gap.count}× · {gap.modules.join(" · ") || "system"}
                {gap.severity && (
                  <>
                    {" · "}
                    {gap.severity}
                  </>
                )}
              </div>
            </ContextBlock>
          )}
          {actor && (
            <ContextBlock label="Accountability">
              <div className="bios-context-headline">{actor.name}</div>
              <div className="bios-context-meta">
                {ROLE_LABEL[actor.role] ?? actor.role} · {actor.module}
                {actor.severity && (
                  <>
                    {" · "}
                    {actor.severity}
                  </>
                )}
              </div>
            </ContextBlock>
          )}
        </div>
      )}
      {mode.geo_phrase && (
        <div className="bios-context-geo">Geo focus · {mode.geo_phrase}</div>
      )}
    </aside>
  );
}

function ContextBlock({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bios-context-block">
      <div className="bios-context-block-label">{label}</div>
      {children}
    </div>
  );
}

// BOTTOM — single expandable evidence drawer. All diagnostic surfaces
// (accountability, causality, projection, gap detail, module grid,
// location, coverage, trend) collapse into one place.
function EvidenceDrawer({ data }: { data: OverviewResponse }) {
  const hasEvidence =
    Boolean(data.accountability?.summary) ||
    Boolean(data.system_causality) ||
    (data.root_causes?.length ?? 0) > 0 ||
    (data.forward_projection?.consequences.length ?? 0) > 0 ||
    (data.gap_summary?.by_kind.length ?? 0) > 0 ||
    (data.accountability?.actors.length ?? 0) > 0 ||
    (data.trend_series?.length ?? 0) >= 2 ||
    Boolean(data.system_impact) ||
    Boolean(data.impact_by_location);
  if (!hasEvidence) return null;
  return (
    <details className="bios-stage-evidence">
      <summary className="bios-stage-evidence-summary">
        <span>Evidence</span>
        <span className="bios-stage-evidence-hint">expand for diagnostics</span>
      </summary>
      <div className="bios-stage-evidence-body">
        {data.accountability?.summary && (
          <AccountabilitySummary text={data.accountability.summary} />
        )}
        {data.root_causes && data.root_causes.length > 0 && (
          <RootCauseBlock causes={data.root_causes} />
        )}
        {data.system_causality && (
          <SystemCausalityBlock causality={data.system_causality} />
        )}
        {data.forward_projection &&
          data.forward_projection.consequences.length > 0 && (
            <ForwardProjectionBlock projection={data.forward_projection} />
          )}
        {data.gap_summary && data.gap_summary.by_kind.length > 0 && (
          <GapPriorityBlock summary={data.gap_summary} />
        )}
        {data.accountability && data.accountability.actors.length > 0 && (
          <AccountabilityActorsBlock actors={data.accountability.actors} />
        )}
        {data.system_impact && <SystemImpactPanel kpi={data.system_impact} />}
        <ModuleComparisonGrid modules={data.modules} />
        {data.impact_by_location && (
          <ImpactByLocationBlock data={data.impact_by_location} />
        )}
        {data.coverage?.is_incomplete && (
          <CoverageBanner coverage={data.coverage} />
        )}
        {data.trend_series && data.trend_series.length >= 2 && (
          <RealityTrendChart series={data.trend_series} />
        )}
      </div>
    </details>
  );
}

function LoadingHero() {
  return (
    <div className="bios-cmd-loading">
      <div className="bios-cmd-loading-pulse" />
      <div className="bios-cmd-loading-text">
        BIOS is reading Media, Capital, Education, and Policy…
      </div>
      <div className="bios-cmd-loading-sub">
        Cross-module aggregation, trend, and impact ranking.
      </div>
    </div>
  );
}

function SystemImpactPanel({ kpi }: { kpi: SystemImpactKpi }) {
  if (kpi.total_items === 0) {
    return (
      <div className="bios-impact">
        <div className="bios-impact-eyebrow">System impact on Black America</div>
        <div className="bios-impact-headline">
          No activity in this window.
        </div>
        <div className="bios-impact-sub">
          Trigger ingestion on the modules below to begin measuring impact.
        </div>
      </div>
    );
  }

  return (
    <div className="bios-impact">
      <div className="bios-impact-eyebrow">System impact on Black America</div>
      <div className="bios-impact-headline">
        <span className="bios-impact-pct">{kpi.black_signals_pct}%</span> of signals
        impacting Black communities ·{" "}
        <span className="bios-impact-pct">{kpi.policy_black_impact_pct}%</span> of
        policy reaches measurable Black impact.
      </div>
      <div className="bios-impact-sub">
        {kpi.measurable_black_items} of {kpi.total_items} cross-module items are both
        Black-relevant AND funded/deployed/enforced/outcome ·{" "}
        {kpi.outcome_gaps} Black-impact gap{kpi.outcome_gaps === 1 ? "" : "s"} detected.
      </div>

      <div className="bios-impact-headline-grid">
        <KpiTile label="Black signals" value={`${kpi.black_signals_pct}%`} accent="accent" />
        <KpiTile
          label="Policy → Black impact"
          value={`${kpi.policy_black_impact_pct}%`}
          accent={kpi.policy_black_impact_pct >= 25 ? "accent" : "warn"}
        />
        <KpiTile
          label="Capital → Black"
          value={`${kpi.capital_black_pct}%`}
          accent={kpi.capital_black_pct >= 30 ? "accent" : "warn"}
        />
        <KpiTile label="Outcome gaps" value={`${kpi.outcome_gaps}`} accent="warn" />
      </div>

      <div className="bios-impact-grid">
        {kpi.modules.map((m) => (
          <div className="bios-impact-block" key={m.module}>
            <div className="bios-impact-block-label">{m.module}</div>
            <div className="bios-impact-rows">
              <ImpactRow
                label="Black-relevant"
                count={m.black_relevant_items}
                total={m.total_items}
                tone="accent"
              />
              <ImpactRow
                label="Measurable"
                count={m.measurable_items}
                total={m.total_items}
                tone={m.measurable_pct >= 25 ? "accent" : "default"}
              />
              <ImpactRow
                label="Total in window"
                count={m.total_items}
                total={m.total_items}
                tone="muted"
              />
            </div>
          </div>
        ))}
      </div>

      <Link
        to={`/app/command?q=${encodeURIComponent(
          "What is reaching Black communities right now, and where is policy or capital failing them?",
        )}`}
        className="bios-impact-cta"
      >
        Investigate in Command Center →
      </Link>
    </div>
  );
}

function KpiTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: "accent" | "warn" | "muted";
}) {
  const color =
    accent === "accent"
      ? "var(--bios-accent)"
      : accent === "warn"
      ? "var(--bios-noise)"
      : "var(--bios-text-faint)";
  return (
    <div className="bios-impact-tile">
      <div className="bios-impact-tile-value" style={{ color }}>{value}</div>
      <div className="bios-impact-tile-label">{label}</div>
    </div>
  );
}

function ImpactRow({
  label,
  count,
  total,
  tone,
}: {
  label: string;
  count: number;
  total: number;
  tone: "default" | "accent" | "muted";
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const barColor =
    tone === "accent"
      ? "var(--bios-accent)"
      : tone === "muted"
      ? "var(--bios-text-faint)"
      : "var(--bios-info)";
  return (
    <div className="bios-impact-row">
      <div className="bios-impact-row-head">
        <span className="bios-impact-row-label">{label}</span>
        <span className="bios-impact-row-count">
          {count} <span style={{ color: "var(--bios-text-faint)" }}>· {pct}%</span>
        </span>
      </div>
      <div className="bios-impact-bar">
        <div
          className="bios-impact-bar-fill"
          style={{ width: `${pct}%`, background: barColor }}
        />
      </div>
    </div>
  );
}

function ImpactByLocationBlock({ data }: { data: ImpactByLocation }) {
  const empty =
    data.top_states_by_impact.length === 0 &&
    data.zero_impact_states.length === 0 &&
    data.top_cities_by_signal.length === 0;
  if (empty) return null;
  return (
    <Card title="Impact by location">
      <div className="bios-loc-grid">
        <div className="bios-loc-block">
          <div className="bios-loc-label">Top states by Black impact</div>
          {data.top_states_by_impact.length === 0 ? (
            <div className="bios-loc-empty">
              No state has produced measurable Black impact in this window.
            </div>
          ) : (
            <ul className="bios-loc-list">
              {data.top_states_by_impact.map((s) => (
                <li key={s.state} className="bios-loc-row">
                  <span className="bios-loc-name">{s.state}</span>
                  <span className="bios-loc-pct">{s.measurable_pct}%</span>
                  <span className="bios-loc-meta">
                    {s.measurable_black_items}/{s.total_items}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bios-loc-block">
          <div className="bios-loc-label">States with zero measurable impact</div>
          {data.zero_impact_states.length === 0 ? (
            <div className="bios-loc-empty">All active states have at least some measurable impact.</div>
          ) : (
            <ul className="bios-loc-list">
              {data.zero_impact_states.map((s) => (
                <li key={s.state} className="bios-loc-row">
                  <span className="bios-loc-name">{s.state}</span>
                  <span className="bios-loc-zero">0</span>
                  <span className="bios-loc-meta">{s.total_items} items</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bios-loc-block">
          <div className="bios-loc-label">Cities with strongest signals</div>
          {data.top_cities_by_signal.length === 0 ? (
            <div className="bios-loc-empty">No cities tagged in this window.</div>
          ) : (
            <ul className="bios-loc-list">
              {data.top_cities_by_signal.map((c) => (
                <li key={`${c.city}-${c.state ?? "x"}`} className="bios-loc-row">
                  <span className="bios-loc-name">
                    {c.city}
                    {c.state && (
                      <span className="bios-loc-state">·{c.state}</span>
                    )}
                  </span>
                  <span className="bios-loc-pct">{c.black_relevant_items}</span>
                  <span className="bios-loc-meta">{c.total_items} items</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

function ModuleComparisonGrid({ modules }: { modules: OverviewModuleView[] }) {
  return (
    <div className="bios-overview-grid">
      {modules.map((m) => (
        <ModuleCard key={m.module} m={m} />
      ))}
    </div>
  );
}

function ModuleCard({ m }: { m: OverviewModuleView }) {
  const arrow = m.trend === "up" ? "↑" : m.trend === "down" ? "↓" : "→";
  const arrowColor =
    m.trend === "up"
      ? "var(--bios-signal)"
      : m.trend === "down"
      ? "var(--bios-noise)"
      : "var(--bios-text-faint)";
  const statusLabel = {
    "noise-heavy": "Noise-heavy",
    mixed: "Mixed",
    "signal-emerging": "Signal emerging",
    quiet: "Quiet",
  }[m.status];
  const statusColor =
    m.status === "signal-emerging"
      ? "var(--bios-signal)"
      : m.status === "noise-heavy"
      ? "var(--bios-noise)"
      : m.status === "mixed"
      ? "var(--bios-mixed)"
      : "var(--bios-text-faint)";

  return (
    <Link to={`/app/${m.module}`} className="bios-overview-mod">
      <div className="bios-overview-mod-head">
        <span className="bios-overview-mod-name">{m.module}</span>
        <span className="bios-overview-mod-trend" style={{ color: arrowColor }}>
          {arrow}
          {Math.abs(m.reality_delta) > 0 && (
            <span className="bios-overview-mod-delta">
              {m.reality_delta > 0 ? "+" : ""}
              {m.reality_delta}
            </span>
          )}
        </span>
      </div>
      <div className="bios-overview-mod-reality">{m.reality_pct}%</div>
      <div className="bios-overview-mod-reality-label">reality</div>
      <div className="bios-overview-mod-status" style={{ color: statusColor }}>
        {statusLabel}
      </div>
      <div className="bios-overview-mod-foot">
        <span>{m.real_pct}% real</span>
        <span>·</span>
        <span>{m.narrative_pct}% narrative</span>
        <span>·</span>
        <span>{m.total_items} items</span>
      </div>
    </Link>
  );
}

function CoverageBanner({ coverage }: { coverage: CoverageInfo }) {
  return (
    <div className="bios-coverage-banner" role="status">
      <span className="bios-coverage-banner-icon" aria-hidden>
        ◌
      </span>
      <div>
        <strong>Impact classification incomplete.</strong>{" "}
        {coverage.unknown_pct}% of items in this window have not been mapped to
        population impact yet. The headline numbers above only reflect classified
        items.
      </div>
    </div>
  );
}

// Phase 20.4
function AccountabilitySummary({ text }: { text: string }) {
  return (
    <div className="bios-acct-summary">
      <span className="bios-acct-summary-label">Accountability</span>
      <span className="bios-acct-summary-text">{text}</span>
    </div>
  );
}

function SystemCausalityBlock({ causality }: { causality: SystemCausality }) {
  return (
    <div className="bios-causality">
      <div className="bios-causality-eyebrow">System causality</div>
      <div className="bios-causality-root">{causality.root_failure_mode}</div>
      {causality.system_links.length > 0 && (
        <ul className="bios-causality-list">
          {causality.system_links.map((l, i) => (
            <li key={i} className={`bios-causality-link bios-sev-${l.severity}`}>
              <div className="bios-causality-link-text">{l.statement}</div>
              <div className="bios-causality-link-meta">
                <span className={`bios-sev-tag bios-sev-tag-${l.severity}`}>
                  {l.severity}
                </span>
                {l.modules.length > 0 && (
                  <span className="bios-causality-link-modules">
                    {l.modules.join(" · ")}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AccountabilityActorsBlock({ actors }: { actors: AccountabilityActor[] }) {
  return (
    <div className="bios-actors">
      <div className="bios-actors-eyebrow">Accountability actors</div>
      <ul className="bios-actors-list">
        {actors.map((a, i) => (
          <li key={i} className={`bios-actor bios-sev-${a.severity}`}>
            <div className="bios-actor-head">
              <span className="bios-actor-name">{a.name}</span>
              <span className="bios-actor-role">{ROLE_LABEL[a.role] ?? a.role}</span>
              <span className="bios-actor-mod">{a.module}</span>
              <span className={`bios-sev-tag bios-sev-tag-${a.severity}`}>
                {a.severity}
              </span>
            </div>
            <div className="bios-actor-involvement">{a.involvement}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ForwardProjectionBlock({
  projection,
}: {
  projection: ForwardProjection;
}) {
  return (
    <div className="bios-forward">
      <div className="bios-forward-eyebrow">If nothing changes</div>
      <ul className="bios-forward-list">
        {projection.consequences.map((c, i) => (
          <li key={i} className="bios-forward-item">
            {c}
          </li>
        ))}
      </ul>
    </div>
  );
}

function GapPriorityBlock({ summary }: { summary: GapSummary }) {
  return (
    <div className="bios-gappri">
      <div className="bios-gappri-head">
        Where the system is failing Black communities
      </div>
      <ul className="bios-gappri-list">
        {summary.by_kind.map((g) => (
          <li key={g.kind} className="bios-gappri-item">
            <GapDetailRow detail={g} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function GapDetailRow({ detail }: { detail: GapDetail }) {
  const label = GAP_LABEL[detail.kind] ?? detail.kind.replace(/_/g, " ");
  return (
    <Link
      to={`/app/command?q=${encodeURIComponent(detail.query)}`}
      className="bios-gappri-link"
    >
      <div className="bios-gappri-link-head">
        <span className="bios-gappri-count">{detail.count}</span>
        <span className="bios-gappri-label">{label}</span>
        {detail.modules.length > 0 && (
          <span className="bios-gappri-modules">{detail.modules.join(" · ")}</span>
        )}
        {detail.severity && (
          <span className={`bios-sev-tag bios-sev-tag-${detail.severity}`}>
            {detail.severity}
          </span>
        )}
        <span className="bios-gappri-arrow" aria-hidden>
          →
        </span>
      </div>
      <div className="bios-gappri-rows">
        <div className="bios-gappri-row">
          <span className="bios-gappri-row-label">What</span>
          <span className="bios-gappri-row-text">{detail.what}</span>
        </div>
        <div className="bios-gappri-row">
          <span className="bios-gappri-row-label">Why</span>
          <span className="bios-gappri-row-text">{detail.why}</span>
        </div>
        <div className="bios-gappri-row">
          <span className="bios-gappri-row-label bios-gappri-row-label-action">
            Action
          </span>
          <span className="bios-gappri-row-text bios-gappri-row-text-action">
            {detail.action}
          </span>
        </div>
      </div>
    </Link>
  );
}

// Phase 21.1: low-data banner — fires when a state/city geo is set but
// the filtered window has fewer than `threshold` items. Offers a
// one-click expand to national context.
function LowDataBanner({
  warning,
  onExpand,
}: {
  warning: LowDataWarning;
  onExpand: () => void;
}) {
  return (
    <div className="bios-lowdata" role="status">
      <span className="bios-lowdata-icon" aria-hidden>◌</span>
      <div className="bios-lowdata-text">
        <strong>Limited data available for {warning.selected_label}.</strong>{" "}
        Only {warning.total_items} item{warning.total_items === 1 ? "" : "s"} in
        this window — below the {warning.threshold}-item threshold for trustworthy
        intelligence reads.
      </div>
      <button type="button" className="bios-lowdata-cta" onClick={onExpand}>
        Expand to national →
      </button>
    </div>
  );
}

function RootCauseBlock({ causes }: { causes: RootCause[] }) {
  return (
    <div className="bios-rootcause">
      <div className="bios-rootcause-eyebrow">Why this is happening</div>
      <ol className="bios-rootcause-list">
        {causes.map((c, i) => (
          <li key={i} className="bios-rootcause-item">
            <span className="bios-rootcause-text">{c.cause}</span>
            {c.modules.length > 0 && (
              <span className="bios-rootcause-modules">
                {c.modules.join(" · ")}
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function RealityTrendChart({ series }: { series: OverviewTrendPoint[] }) {
  // Hand-rolled SVG. Two polylines: real (gold) vs narrative (red).
  if (series.length < 2) {
    return (
      <Card title="Trend · real vs narrative">
        <p style={{ margin: 0, color: "var(--bios-text-muted)", fontSize: 13 }}>
          Not enough data points yet.
        </p>
      </Card>
    );
  }
  const W = 720;
  const H = 180;
  const PAD_X = 12;
  const PAD_Y = 14;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y * 2;
  const max = Math.max(
    1,
    ...series.map((p) => Math.max(p.real, p.narrative)),
  );
  const x = (i: number) =>
    PAD_X + (series.length === 1 ? 0 : (i / (series.length - 1)) * innerW);
  const y = (v: number) => PAD_Y + innerH - (v / max) * innerH;
  const realPath = series.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.real)}`).join(" ");
  const narrativePath = series
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.narrative)}`)
    .join(" ");

  const firstDate = series[0]?.date ?? "";
  const lastDate = series[series.length - 1]?.date ?? "";

  return (
    <Card title="Trend · real vs narrative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="bios-overview-chart"
        role="img"
        aria-label="Real activity vs narrative activity over time"
      >
        <line
          x1={PAD_X}
          x2={W - PAD_X}
          y1={H - PAD_Y}
          y2={H - PAD_Y}
          stroke="var(--bios-border)"
          strokeWidth="1"
        />
        <path d={realPath} fill="none" stroke="var(--bios-accent)" strokeWidth="2" />
        <path d={narrativePath} fill="none" stroke="var(--bios-noise)" strokeWidth="2" />
      </svg>
      <div className="bios-overview-chart-foot">
        <span>
          <span className="bios-overview-chart-swatch" style={{ background: "var(--bios-accent)" }} />
          Real
        </span>
        <span>
          <span className="bios-overview-chart-swatch" style={{ background: "var(--bios-noise)" }} />
          Narrative
        </span>
        <span style={{ marginLeft: "auto", color: "var(--bios-text-faint)", fontSize: 11 }}>
          {firstDate} → {lastDate}
        </span>
      </div>
    </Card>
  );
}
